import { ChildId, ChildPaymentType, ChildTransactionPayment, PaymentId } from 'types';
import { commonTexts, settlementTexts } from 'consts/text';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useParams } from 'react-router-dom';
import { Option } from 'components/atoms';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { ChildTransactionTable, RemoveModal } from 'components/organisms';
import { useState } from 'react';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useSearchParamsBasedPagination } from 'hooks/useSearchParamsBasedPagination/useSearchParamsBasedPagination';
import { parsePaginationDataToTablePaginationData } from 'utils/parsers/table/parsePaginationDataToTablePaginationData';
import { ChildAddEditBillModal } from '../ChildAddBillModal/ChildAddEditBillModal';

type Props = {
    refetchBalance: () => Promise<void>;
};

export const ChildTransactions = ({ refetchBalance }: Props) => {
    const { displaySuccess, displayError } = useSimpleNotification();
    const { currentPage, currentPageSize, handlePaginationValuesChange } =
        useSearchParamsBasedPagination();

    const [selectedToRevertTransactionId, setSelectedToRevertTransactionId] =
        useState<PaymentId | null>(null);
    const [addBillModalOpen, setAddBillModalOpen] = useState<{
        childId: ChildId;
        transactionId: PaymentId | null;
    } | null>(null);

    const { childId } = useParams<{ childId: ChildId }>();

    const {
        data: childPaymentData,
        isRefetching: isChildPaymentRefetching,
        isInitialLoading: isChildPaymentInitialLoading,
        isError: isChildPaymentError,
    } = useAppQuery('CHILD_SETTLEMENT_PAYMENT', [childId, currentPage, currentPageSize], () =>
        StaffHTTPService.settlements.getChildIncomes({
            childId: childId as ChildId,
            paginationParams: { page: currentPage, size: currentPageSize },
        }),
    );

    const { mutate: removeTransaction, isLoading: isRemoveTransactionLoading } = useAppMutation(
        StaffHTTPService.settlements.removeChildTransaction,
        {
            key: ['REMOVE_TRANSACTION'],
            onSuccess: async () => {
                displaySuccess(settlementTexts.children.successfullyRevertTransactionMessage);
                setSelectedToRevertTransactionId(null);

                await refetchBalance();
            },
            onError: () => displayError(commonTexts.errorMessages.unknown),
            invalidateQueryKeys: [
                ['SETTLEMENTS'],
                ['CHILD_SETTLEMENT'],
                ['CHILD_SETTLEMENT_PAYMENT'],
            ],
        },
    );

    const onAddClick = () => {
        setAddBillModalOpen({ childId: childId as ChildId, transactionId: null });
    };

    const onEditClick = (transactionId: PaymentId) => {
        setAddBillModalOpen({ childId: childId as ChildId, transactionId });
    };

    const onRevertTransactionOk = () => {
        removeTransaction({ transactionId: selectedToRevertTransactionId as PaymentId });
    };
    const onRevertTransactionCancel = () => {
        setSelectedToRevertTransactionId(null);
    };

    const transactionOptions: Option<ChildTransactionPayment>[] = [
        {
            label: commonTexts.actionLabels.edit,
            onClick: (record) => onEditClick(record.id),
            checkIfVisible: (record) =>
                record.type === ChildPaymentType.PAYMENT || record.type === ChildPaymentType.COST,
            Icon: <IconEdit />,
        },
        {
            label: commonTexts.actionLabels.revert,
            onClick: (record) => {
                setSelectedToRevertTransactionId(record.id);
            },
            checkIfVisible: (record) =>
                record.type === ChildPaymentType.PAYMENT || record.type === ChildPaymentType.COST,
            Icon: <IconTrash />,
        },
    ];

    return (
        <>
            <ChildAddEditBillModal
                mode={addBillModalOpen?.transactionId ? 'edit' : 'add'}
                childId={addBillModalOpen?.childId}
                transactionId={addBillModalOpen?.transactionId}
                onCancel={() => setAddBillModalOpen(null)}
            />
            <RemoveModal
                open={!!selectedToRevertTransactionId}
                title={settlementTexts.children.paymentRevert}
                confirmActionLoading={isRemoveTransactionLoading}
                onOk={onRevertTransactionOk}
                onCancel={onRevertTransactionCancel}
            />
            <ChildTransactionTable
                isError={isChildPaymentError}
                isDataInitialLoading={isChildPaymentInitialLoading}
                isDataRefetching={isChildPaymentRefetching}
                options={transactionOptions}
                dataSource={childPaymentData?.content}
                pagination={{
                    ...parsePaginationDataToTablePaginationData({
                        currentPage,
                        currentPageSize,
                    }),
                    total: childPaymentData?.totalElements || 0,
                    onChange: handlePaginationValuesChange,
                }}
                onAddButtonClick={onAddClick}
            />
        </>
    );
};
